import {all, call, fork, put, select, takeLatest} from 'redux-saga/effects'
import types from '../actions/types'
import * as diaryActions from '../actions/diary'
import {moveActivityFailure, moveActivitySuccess} from 'actions/diary'
import * as pivotTableActions from '../actions/pivotTable'
import {generalError} from '../actions/index'
import {isNil} from 'lodash'
import moment from 'moment'
import {createPivotTableWatcher} from './pivotTable'
import {snackbarMessage} from 'actions/snackbar'
import * as diarySelectors from '../selectors/diary'
import * as dialogActions from '../actions/dialog'
import * as onboardingActions from '../actions/onboarding'
import {SKILLS} from 'helpers/onboarding'
import iconNames from 'theme/icons'
import {getInvoiceFinancialYearStart} from 'selectors/settings'
import {getDiaryActivities} from 'selectors/diary'
import {getDiaryParams} from 'helpers/diary'
import {diaryContextActivitiesPivotTable} from 'reducers/diaryContext/activities'
import {t} from "helpers/language"

export const activityFields = {
  default: true,
  company: true,
  activitytype: true,
  attendees: {
    default: true,
    contact: true,
  },
  owner: {
    default: true,
    contact: true,
  },
  creator: true,
  activityUpdates: {
    default: true,
    contact: {
      fullName: true,
    },
  },
}

export const fullDiaryFields = {
  default: true,
  activitytype: true,
  activityresource: true,
  company: {
    id: true,
    name: true,
  },
  contact: {
    id: true,
    companyId: true,
    forename: true,
    surname: true,
    phone: true,
    mobile: true,
    email: true,
  },
  priority: {
    id: true,
  },
  creator: {
    id: true,
    companyId: true,
    forename: true,
    surname: true,
  },
  order: true,
  opportunity: true,
  attendees: {
    default: true,
    contact: true,
  },
  owner: {
    default: true,
    contact: true,
  },
  activityUpdates: true,
}

export default (api) => {


  function startEndRange(selectedDate, diaryRange, type) {
    const rangeMomentUnit = (diaryRange === 'week') ? 'isoWeek' : diaryRange
    const rangeMomentUnits = diaryRange + "s"
    const rangeDate = (!isNil(selectedDate)) ? selectedDate : new Date()
    const rangeStart = moment(rangeDate).startOf(rangeMomentUnit)
    const rangeEnd = moment(rangeStart).add(1, rangeMomentUnits)
    if (type === 'start') {
      return rangeStart.valueOf()
    } else if (type === 'end') {
      return rangeEnd.valueOf()
    }
  }

  const activitiesPivotTable = createPivotTableWatcher(
    api.activity,
    diaryContextActivitiesPivotTable,
    [
      types.DIARY_ACTIVITIES_UPDATE_FILTERS,
      types.DIARY_LOAD_ACTIVITIES_LIST_ATTEMPT,
      types.DIARY_UPDATE_USERS,
      types.DIARY_UPDATE_USERS_LIST,
      types.DIARY_UPDATE_CONTACTS,
      types.DIARY_UPDATE_CONTACTS_LIST,
      types.DIARY_UPDATE_COMPANIES,
      types.DIARY_UPDATE_COMPANIES_LIST,
      types.DIARY_UPDATE_REFERENCES,
      types.DIARY_UPDATE_REFERENCES_LIST,
      types.DIARY_UPDATE_SELECTED_DATE,
      types.DIARY_UPDATE_DIARY_RANGE,
      types.DIARY_UPDATE_DIARY_RANGE_LIST,
      types.DIARY_UPDATE_ACTIVITY_STATUSES,
      types.DIARY_UPDATE_ACTIVITY_STATUSES_LIST,
      types.DIARY_UPDATE_ACTIVITY_TYPE,
      types.DIARY_UPDATE_ACTIVITY_TYPE_LIST,
      types.DIARY_UPDATE_REJECTED_ACTIVITIES,
      types.DIARY_UPDATE_REJECTED_ACTIVITIES_LIST,
      types.DIARY_UPDATE_COMPLETED_ACTIVITIES,
      types.DIARY_UPDATE_COMPLETED_ACTIVITIES_LIST,
    ],
    state => {
      const activityFilters = getDiaryActivities(state)
      const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
      const diaryParams = getDiaryParams(activityFilters, invoiceFinancialYearStart)
      return {
        ...diaryParams,
        includeUnscheduled: true,
      }
    },
    fullDiaryFields
  )


  function* loadActivityCount({startDate}) {
    try {
      // Add +/- 15 days to show events outside the scope of the month too
      const date = Date.parse(startDate)
      const sDate = moment(date).startOf('month').subtract(15, 'days').valueOf()
      const endDate = moment(date).startOf('month').add(1, 'months').add(15, 'days').valueOf()

      const response = yield call(api.activity.getSummary, {startDate: sDate, endDate}, {})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(diaryActions.loadActivityCountSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(diaryActions.loadActivityCountFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* moveActivity() {
    try {
      yield put(snackbarMessage('Moving activity...', 'loading', false, false))

      const activity = yield select(state => state.diaryContext.activities.moveActivity.activity.id)
      const allDay = yield select(state => state.diaryContext.activities.moveActivity.activity.allDay)
      const start = yield select(state => state.diaryContext.activities.moveActivity.startTime)
      const end = yield select(state => state.diaryContext.activities.moveActivity.endTime)
      const startTime = moment(start).valueOf()
      const endTime = moment(end).valueOf()
      const selectedDate = yield select(state => state.diaryContext.activities.selectedDate)

      const params = {
        id: activity,
        startDate: startTime,
        endDate: endTime,
        allDay: allDay,
      }

      const response = yield call(api.activity.updateDates, params, activityFields)
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(moveActivitySuccess(params))
          yield put(snackbarMessage('Activity moved', iconNames.tick))
          yield put(diaryActions.loadActivities())
          yield put(diaryActions.loadActivityCount(selectedDate))

        },
        function* onFail(key, message, code) {
          yield put(moveActivityFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* updateTask(params) {
    try {
      const {activityData, closeDialogOnCommit, addData} = params.activityUpdate
      activityData.timeSpent = activityData.timeSpent ? activityData.timeSpent.value : 0

      const response = yield call(api.activity.updateAdd, {activityUpdate: activityData}, {})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(diaryActions.addActivitySuccess(data))
          yield put(snackbarMessage('Your update has been added to this task', iconNames.tick, true, true))

          const isCalendarActive = yield select(diarySelectors.isDiaryCalendarActive)
          const isListActive = yield select(diarySelectors.isDiaryListActive)

          if (isCalendarActive) yield put(diaryActions.loadActivities())
          if (isListActive) yield put(diaryActions.loadActivitiesList())


          if (!closeDialogOnCommit) {
            addData.result[0].activityId = addData.result[0].id
            yield put(dialogActions.loadActivity(addData.result[0].id))
          } else {
            yield put(dialogActions.hideAddActivityDialog())
          }
          //if (callback) callback({ success: true, data: addData.result })

        },
        function* onFail(key, message, code) {
          yield put(snackbarMessage('Error: Could not add activity task update'))
          yield put(diaryActions.addActivityFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* editActivityUpdate({activityUpdate}) {
    try {
      const response = yield call(api.activity.updateUpdate, {activityUpdate}, {})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(diaryActions.editActivityUpdateSuccess(data))
          yield put(snackbarMessage('Changes saved', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(snackbarMessage('Error: Could not save changes'))
          yield put(diaryActions.editActivityUpdateFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addActivity({activity, activityUpdate, closeDialogOnCommit, threadData}) {
    try {
      const response = yield call(api.activity.add, {activity}, activityFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const activity = data.result[0]

          yield put(diaryActions.addActivitySuccess(data.result))
          yield put(snackbarMessage(t('Your new activity has been added'), iconNames.tick))

          // Post the task update
          if (activityUpdate) {
            if (activity) yield put(diaryActions.updateTask({
              activityData: {
                ...activityUpdate,
                activityId: activity.id,
              },
              closeDialogOnCommit,
              addData: data,
            }))
          } else {
            const isCalendarActive = yield select(diarySelectors.isDiaryCalendarActive)
            const isListActive = yield select(diarySelectors.isDiaryListActive)

            if (isCalendarActive) yield put(diaryActions.loadActivities())
            if (isListActive) yield put(diaryActions.loadActivitiesList())

            if (!closeDialogOnCommit) {
              yield put(dialogActions.loadActivity(activity.id))
              if (activity.threadRef) yield put(diaryActions.loadActivityThread(activity.threadRef))
            } else {
              yield put(dialogActions.hideAddActivityDialog())
            }
          }

          // Progress onboarding skill
          yield put(onboardingActions.skillPage(SKILLS.ADD_ACTIVITY.key, 3))

          // Raise another activity in the thread
          if (threadData) {
            yield put(dialogActions.showAddActivityDialog({
              ...threadData,
              threadRef: activity.id,
            }))
          }
        },
        function* onFail(key, message, code) {
          yield put(diaryActions.addActivityFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* editActivity({activity, callback}) {
    try {
      const params = {activity}
      const response = yield call(api.activity.update, params, activityFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(diaryActions.editActivitySuccess(data.result))
          yield put(snackbarMessage(t('The activity has been updated'), iconNames.tick))

          const isCalendarActive = yield select(diarySelectors.isDiaryCalendarActive)
          const isListActive = yield select(diarySelectors.isDiaryListActive)

          if (isCalendarActive) yield put(diaryActions.loadActivities())
          if (isListActive) yield put(diaryActions.loadActivitiesList())

          if (callback) callback({success: true, data: data.result})
        },
        function* onFail(key, message, code) {
          yield put(diaryActions.editActivityFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* deleteActivity({activityId, series = false, from}) {
    try {
      const response = yield call(api.activity.delete, {id: activityId, series, from})
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(diaryActions.deleteActivitySuccess(activityId))

          yield put(dialogActions.hideEditActivityDialog())

          const isCalendarActive = yield select(diarySelectors.isDiaryCalendarActive)
          const isListActive = yield select(diarySelectors.isDiaryListActive)

          if (isCalendarActive) yield put(diaryActions.loadActivities())
          if (isListActive) yield put(diaryActions.loadActivitiesList())

          yield put(snackbarMessage(t('Your activity has been deleted'), iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(diaryActions.deleteActivityFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadActivities() {
    try {
      const activityFilters = yield select(getDiaryActivities)
      const invoiceFinancialYearStart = yield select(getInvoiceFinancialYearStart)
      const expandRange = yield select(diarySelectors.isDiaryCalendarActive)
      const diaryParams = getDiaryParams(activityFilters, invoiceFinancialYearStart, expandRange)

      const response = yield call(api.activity.get, diaryParams, fullDiaryFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(diaryActions.loadActivitiesSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(diaryActions.loadActivitiesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  const ordersPivotTable = createPivotTableWatcher(
    api.quote,
    'diaryContext.orders.pivotTable',
    [types.DIARY_ORDERS_UPDATE_FILTERS],
    state => {
      return {
        rangeStart: startEndRange(
          state.diaryContext.orders.selectedDate, state.diaryContext.activities.diaryRange, 'start'
        ),
        rangeEnd: startEndRange(
          state.diaryContext.orders.selectedDate, state.diaryContext.activities.diaryRange, 'end'
        ),
        contactId: state.diaryContext.orders.contacts,
        companyId: state.diaryContext.orders.companies,
        opportunityId: state.diaryContext.orders.references,
        status: state.diaryContext.orders.orderState !== 5 ? state.diaryContext.orders.orderState : [],
        productionStatus: state.diaryContext.orders.orderState === 5 ? 0 : [],
        created: state.diaryContext.orders.orderState === 0 ? 0 : [],
        quoted: state.diaryContext.orders.orderState === 1 ? 1 : [],
        confirmed: state.diaryContext.orders.orderState === 2 ? 2 : [],
        delivered: state.diaryContext.orders.orderState === 3 ? 3 : [],
        invoiced: state.diaryContext.orders.orderState === 4 ? 4 : [],
        required: state.diaryContext.orders.orderState === 5 ? 5 : [],
        target: state.diaryContext.orders.orderState === 6 ? 6 : [],
        showInactive: state.diaryContext.orders.orderState >= 4,
        showCompleted: state.diaryContext.orders.orderState === 6,
      }
    }, {...fullDiaryFields}
  )

  function* loadOrders() {
    try {
      const contacts = yield select(state => state.diaryContext.orders.contacts)
      const companies = yield select(state => state.diaryContext.orders.companies)
      const references = yield select(state => state.diaryContext.orders.references)
      const orderState = yield select(state => state.diaryContext.orders.orderState)
      const selectedDate = yield select(state => state.diaryContext.orders.selectedDate)
      const diaryRange = yield select(state => state.diaryContext.activities.diaryRange)

      const params = {
        rangeStart: startEndRange(selectedDate, diaryRange, 'start'),
        rangeEnd: startEndRange(selectedDate, diaryRange, 'end'),
        contactId: (!isNil(contacts) && contacts.length !== 0) ? contacts.map((contact) => {
          return contact.value
        }) : [],
        companyId: (!isNil(companies) && companies.length !== 0) ? companies.map((company) => {
          return company.value
        }) : [],
        opportunityId: (!isNil(references) && references.length !== 0) ? references.map((reference) => {
          return reference.id
        }) : [],
        status: orderState !== 5 ? orderState : [],
        productionStatus: orderState === 5 ? 0 : [],
        created: orderState === 0 ? 0 : [],
        quoted: orderState === 1 ? 1 : [],
        confirmed: orderState === 2 ? 2 : [],
        delivered: orderState === 3 ? 3 : [],
        invoiced: orderState === 4 ? 4 : [],
        required: orderState === 5 ? 5 : [],
        target: orderState === 6 ? 6 : [],
        showInactive: orderState >= 4,
        showCompleted: orderState === 6,
      }

      const response = yield call(api.quote.get, params, fullDiaryFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(diaryActions.loadOrdersSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(diaryActions.loadOrdersFailure(message, code))
        }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadOrdersList() {
    try {
      const contacts = yield select(state => state.diaryContext.orders.contacts)
      const companies = yield select(state => state.diaryContext.orders.companies)
      const references = yield select(state => state.diaryContext.orders.references)
      const orderState = yield select(state => state.diaryContext.orders.orderState)
      const selectedDate = yield select(state => state.diaryContext.orders.selectedDate)
      const diaryRange = yield select(state => state.diaryContext.activities.diaryRange)

      const params = {
        rangeStart: startEndRange(selectedDate, diaryRange, 'start'),
        rangeEnd: startEndRange(selectedDate, diaryRange, 'end'),
        contactId: (!isNil(contacts) && contacts.length !== 0) ? contacts.map((contact) => {
          return contact.value
        }) : [],
        companyId: (!isNil(companies) && companies.length !== 0) ? companies.map((company) => {
          return company.value
        }) : [],
        opportunityId: (!isNil(references) && references.length !== 0) ? references.map((reference) => {
          return reference.id
        }) : [],
        status: orderState !== 5 ? orderState : [],
        productionStatus: orderState === 5 ? 0 : [],
        created: orderState === 0 ? 0 : [],
        quoted: orderState === 1 ? 1 : [],
        confirmed: orderState === 2 ? 2 : [],
        delivered: orderState === 3 ? 3 : [],
        invoiced: orderState === 4 ? 4 : [],
        required: orderState === 5 ? 5 : [],
        target: orderState === 6 ? 6 : [],
        showInactive: orderState >= 4,
        showCompleted: orderState === 6,
      }

      const response = yield call(api.quote.get, params, fullDiaryFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const pivotTableStateKey = "diaryContext.orders.pivotTable"
          const orders = data.result
          yield put(pivotTableActions.loadDataSuccess(pivotTableStateKey, orders, orders.length))
        },
        function* onFail(key, message, code) {
          yield put(diaryActions.loadOrdersListFailure(message, code))
        }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadActivityThread({threadRef}) {
    const activityFields = {
      default: true,
      company: true,
      activitytype: true,
      attendees: {
        default: true,
        contact: true,
      },
      owner: {
        default: true,
        contact: true,
      },
      creator: true,
      activityUpdates: {
        default: true,
        contact: {
          fullName: true,
        },
      },
    }

    const response = yield call(api.activity.get, {thread: threadRef}, activityFields)
    yield api.handleResponse(
      response,
      function* onOk(key, data) {
        yield put(diaryActions.loadActivityThreadSuccess(data.result))
      },
      function* onFail(key, message, code) {
        yield put(diaryActions.loadActivityThreadFailure(message, code))
      }
    )
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.DIARY_UPDATE_TASK, updateTask),
      takeLatest(types.DIARY_LOAD_ACTIVITY_COUNT_ATTEMPT, loadActivityCount),
      takeLatest(types.DIARY_MOVE_ACTIVITY_ATTEMPT, moveActivity),
      takeLatest(types.DIARY_ADD_ACTIVITY_ATTEMPT, addActivity),
      takeLatest(types.DIARY_EDIT_ACTIVITY_ATTEMPT, editActivity),
      takeLatest(types.DIARY_EDIT_ACTIVITY_UPDATE_ATTEMPT, editActivityUpdate),
      takeLatest(types.DIARY_DELETE_ACTIVITY_ATTEMPT, deleteActivity),
      takeLatest(types.DIARY_LOAD_ACTIVITIES_ATTEMPT, loadActivities),
      takeLatest(types.DIARY_UPDATE_USERS, loadActivities),
      takeLatest(types.DIARY_UPDATE_CONTACTS, loadActivities),
      takeLatest(types.DIARY_UPDATE_COMPANIES, loadActivities),
      takeLatest(types.DIARY_UPDATE_REFERENCES, loadActivities),
      takeLatest(types.DIARY_UPDATE_SELECTED_DATE, loadActivities),
      takeLatest(types.DIARY_UPDATE_DIARY_RANGE, loadActivities),
      takeLatest(types.DIARY_UPDATE_ACTIVITY_TYPE, loadActivities),
      takeLatest(types.DIARY_UPDATE_ACTIVITY_STATUSES, loadActivities),
      takeLatest(types.DIARY_UPDATE_REJECTED_ACTIVITIES, loadActivities),
      takeLatest(types.DIARY_UPDATE_COMPLETED_ACTIVITIES, loadActivities),
      takeLatest(types.DIARY_LOAD_ORDERS_ATTEMPT, loadOrders),
      takeLatest(types.DIARY_UPDATE_ORDER_CONTACTS, loadOrders),
      takeLatest(types.DIARY_UPDATE_ORDER_COMPANIES, loadOrders),
      takeLatest(types.DIARY_UPDATE_ORDER_SELECTED_DATE, loadOrders),
      takeLatest(types.DIARY_UPDATE_ORDER_REFERENCES, loadOrders),
      takeLatest(types.DIARY_UPDATE_ORDER_STATE, loadOrders),
      takeLatest(types.DIARY_LOAD_ORDERS_LIST_ATTEMPT, loadOrdersList),
      takeLatest(types.DIARY_UPDATE_ORDER_CONTACTS, loadOrdersList),
      takeLatest(types.DIARY_UPDATE_ORDER_COMPANIES, loadOrdersList),
      takeLatest(types.DIARY_UPDATE_ORDER_SELECTED_DATE, loadOrdersList),
      takeLatest(types.DIARY_UPDATE_ORDER_REFERENCES, loadOrdersList),
      takeLatest(types.DIARY_UPDATE_ORDER_STATE, loadOrdersList),
      takeLatest(types.DIARY_LOAD_ACTIVITY_THREAD_ATTEMPT, loadActivityThread),
    ])
  }

  function* watcher() {
    yield fork(myWatcher)
    yield fork(activitiesPivotTable)
    yield fork(ordersPivotTable)
  }

  return {
    watcher,
  }
}
